@if (loading) {
  <app-rings-loader></app-rings-loader>
}

@if (!loading && !labDetails) {
  <div class="mt-5 d-flex align-items-center justify-content-center">
    <mat-form-field appearance="outline" class="w-100 px-5">
      <mat-label>{{'Select Lab' | translate}}</mat-label>
      <mat-select [(ngModel)]="selectedLabId" (selectionChange)="showSampleSelectionTable()">
        @for (option of labsList; track trackById($index, option)) {
          <mat-option [value]="option.id">
            {{ option.name }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
  </div>
}

@if (stepperConfig && labDetails) {
  <app-stepper-container [config]="stepperConfig"
    (stepChanged)="onStepChanged($event)">
  </app-stepper-container>
}

<ng-template #sampling>
  <div class="sample-status form-only-content">
    <div [ngStyle]="{'background-color': sampleStatusDetails.color}">
      {{sampleStatusDetails.label | translate}}
    </div>
    @if (exemptedSamples.length || blankRuleViolated) {
      <div class="sample-status-alerts">
        @if (exemptedSamples.length) {
          <div class="sample-exemption-alert alert-danger d-flex align-items-center">
            <span><mat-icon>warning</mat-icon></span>
            <div>
              <span class="font-bold text-red-600">{{ 'Warning:' | translate }}</span> {{ 'Sample Number –' | translate }}
              @for (sample of exemptedSamples; track trackById(i, sample); let i = $index; let last = $last) {
                <b>{{ sample.sampleNumber }}</b>@if (!last) {
                <span>{{ i === exemptedSamples.length - 2 ? ' ' + ('add' | translate) + ' ':
                ', ' }}</span>
              }
            }
            {{ 'have been removed as they do not meet the required status of Awaiting Lab Submission' | translate }}
          </div>
        </div>
      }
      @if (blankRuleViolated) {
        <div class="sample-exemption-alert alert-danger d-flex align-items-center">
          <span><mat-icon>warning</mat-icon></span>
          <div>
            <span class="font-bold text-red-600">{{ 'Warning:' | translate }}</span>
            @if (blankRuleBelowCount) {
              <div>
                {{ 'This Lab Requisition requires at least {{count}} blank sample(s) but has {{actual}}.'
                | translate: { count: effectiveMinBlankCount, actual: blankCount } }}
              </div>
            }
            @if (blankRuleBelowRatio) {
              <div>
                {{ 'Blank samples must be at least {{ratio}}% of samples on this requisition.'
                | translate: { ratio: effectiveMinBlankRatioPercent } }}
              </div>
            }
          </div>
        </div>
      }
    </div>
  }
</div>
<div class="tableColStyle custom-theme-table">
  <div class="custom-scrollbar sticky-header table-shadow h-auto">
    <table mat-table [dataSource]="isFinalStep ? selectedSamples :samplesList"
      class="h-100 table table-striped">
      <tr class="mat-row" *matNoDataRow>
        <td class="mat-cell text-center" [attr.colspan]="sampleColumns.length">
          @if (data?.isFromSamples) {
            <b>
              {{'Select samples with status Awaiting Lab Submission and try again' | translate}}
            </b>
          } @else {
            {{'No data matching the filter' | translate}}
          }
          <ng-template #noData>{{'No data matching the filter' | translate}}</ng-template>
        </td>
      </tr>
      <ng-container matColumnDef="id">
        <th mat-header-cell *matHeaderCellDef class="form-only-content">
          <mat-checkbox (change)="toggleAll($event.checked)" [checked]="isAllSelected()"
            [indeterminate]="selectedSamples.length > 0 && !isAllSelected()">
          </mat-checkbox>
        </th>
        <td mat-cell *matCellDef="let row" class="form-only-content">
          <mat-checkbox (change)="toggleSelection(row, $event.checked)" [checked]="isSelected(row)">
          </mat-checkbox>
        </td>
      </ng-container>
      <ng-container matColumnDef="sampleNumber">
        <th class="left-aligned" mat-header-cell *matHeaderCellDef>{{'Sample Number' | translate}}</th>
        <td class="left-aligned cursor-pointer" mat-cell *matCellDef="let element"
          (click)="openSampleDetail(element)">
          @if (isFinalStep && element?.barcodeContent) {
            <div class="d-flex justify-content-center">{{element.sampleNumber}}</div>
            <div class="barcode-container">
              <app-barcode [value]="element.barcodeContent"></app-barcode>
            </div>
          } @else {
            {{ element.sampleNumber }}
          }
          <ng-template #sampleNumber> {{ element.sampleNumber }}</ng-template>
        </td>
      </ng-container>
      <ng-container matColumnDef="alternateSampleNumber">
        <th mat-header-cell *matHeaderCellDef>{{ alternateSampleLabel }}</th>
        <td mat-cell *matCellDef="let element">
          {{ element.alternateSampleNumber || '-' }}
        </td>
      </ng-container>
      <ng-container matColumnDef="masterAgentName">
        <th mat-header-cell *matHeaderCellDef>{{'Agent Name' | translate}}</th>
        <td mat-cell *matCellDef="let element">
          {{ element.agentNamesStr }}
        </td>
      </ng-container>
      <ng-container matColumnDef="agentGroupId">
        <th mat-header-cell *matHeaderCellDef>{{'Agent Group' | translate}}</th>
        <td mat-cell *matCellDef="let element">{{ element.agentGroupName }}</td>
      </ng-container>
      <ng-container matColumnDef="startDate">
        <th mat-header-cell *matHeaderCellDef>{{'Sampling Date' | translate}}</th>
        <td mat-cell *matCellDef="let element">{{ element.samplingDateStr }}</td>
      </ng-container>
      <ng-container matColumnDef="sampleVolume">
        <th mat-header-cell *matHeaderCellDef>{{'Sample Volume (L)' | translate}}</th>
        <td mat-cell *matCellDef="let element">{{ element.volumeLitersStr }}</td>
      </ng-container>
      <ng-container matColumnDef="runTime">
        <th mat-header-cell *matHeaderCellDef>{{'Total Time (Minutes)' | translate}}</th>
        <td mat-cell *matCellDef="let element">{{ element.runTimeMinutesStr }}</td>
      </ng-container>
      <ng-container matColumnDef="flowRate">
        <th mat-header-cell *matHeaderCellDef>{{'Flowrate (L/min)' | translate}}</th>
        <td mat-cell *matCellDef="let element">{{ element.flowRateLPerMinStr }}</td>
      </ng-container>
      <ng-container matColumnDef="analyticalMethodName">
        <th mat-header-cell *matHeaderCellDef>{{'Method Reference' | translate}}</th>
        <td mat-cell *matCellDef="let element">{{ element.analyticalMethodName || '-' }}</td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="sampleColumns"></tr>
      <tr mat-row *matRowDef="let row; let even = even; columns: sampleColumns;" [class.even-row]="even">
      </tr>
    </table>
  </div>
</div>
</ng-template>

<ng-template #accountInfo>
  <ng-container *ngTemplateOutlet="accountInfoBase; context: { $implicit: { showAlsQuote: false } }"></ng-container>
</ng-template>

<ng-template #account>
  <mat-form-field appearance="outline" class="w-50 form-only-content">
    <mat-label>{{'Lab Requisition Number' | translate}}</mat-label>
    <input matInput required maxlength="20" [(ngModel)]="labRequisitionFields.labRequisitionNumber"
      [disabled]="$safeNavigationMigration(labRequisitionFields?.isAutogenerated)" />
    </mat-form-field>
    @if (isFinalStep) {
      <div class="d-flex">
        <div class="pb-2 material-grey-icon"> {{'Lab Requisition Number' | translate}}:</div>
        <span class="pl-1"> {{labRequisitionFields.labRequisitionNumber}} </span>
      </div>
    }
    <div class="account-form d-flex flex-column">
      <mat-form-field appearance="outline">
        <mat-label>{{'Account Information' | translate}}</mat-label>
        <input matInput [(ngModel)]="labRequisitionFields.accountNumber" />
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>{{'Report To' | translate}}</mat-label>
        <input matInput [(ngModel)]="labRequisitionFields.reportTo" />
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>{{'Comments And Instructions' | translate}}</mat-label>
        <textarea matInput [(ngModel)]="labRequisitionFields.reportToComments"
          [placeholder]="'Add Comments And Instructions Here' | translate" rows="4">
        </textarea>
      </mat-form-field>
    </div>
  </ng-template>

  <ng-template #alsQuote>
    <ng-container *ngTemplateOutlet="accountInfoBase; context: { $implicit: { showAlsQuote: true } }"></ng-container>
  </ng-template>

  <ng-template #tat>
    <div class="tableColStyle custom-theme-table px-1">
      <div class="custom-scrollbar sticky-header table-shadow h-auto">
        <table mat-table [dataSource]="labDetails.tats" class="h-100 table table-striped">
          <ng-container matColumnDef="turnAroundTime">
            <th mat-header-cell *matHeaderCellDef class="left-aligned">{{'Turn Around Time' | translate}}</th>
            <td mat-cell *matCellDef="let element" class="left-aligned pl-20">
              <mat-radio-button class="form-only-content" (change)="selectTat(element)"
                [checked]="isTatSelected(element)">
              </mat-radio-button>
              @if (isFinalStep && isTatSelected(element)) {
                <span class="checked-icon"
                  [attr.aria-label]="'Selected' | translate">
                </span>
              }
              {{ element.turnAroundTime }}
            </td>
          </ng-container>
          <ng-container matColumnDef="surcharge">
            <th mat-header-cell *matHeaderCellDef>{{'Surcharge' | translate}}</th>
            <td mat-cell *matCellDef="let element">{{ element.surcharge }}</td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="tatColumns"></tr>
          <tr mat-row *matRowDef="let row; let even = even; columns: tatColumns;" [class.even-row]="even">
          </table>
        </div>
      </div>
    </ng-template>

    <ng-template #collection>
      <div class="collection-form flex-container mt-4">
        <div class="column">
          <div>{{'Site Name' | translate}}: <span>{{shownSampleDetails?.siteNames}}</span></div>
          <div>{{'Survey' | translate}}: <span>{{shownSampleDetails?.surveyNames}}</span></div>
          @if (isFinalStep) {
            <div>
              <div class="pb-2"> {{'General Comments' | translate}}:</div>
              <span>{{labRequisitionFields.generalComments}}</span>
            </div>
          }

          <mat-form-field appearance="outline" class="form-only-content">
            <mat-label>{{'General Comments' | translate}}</mat-label>
            <textarea matInput [(ngModel)]="labRequisitionFields.generalComments" rows="3"
            [placeholder]="'Add Comments And Instructions Here' | translate"></textarea>
          </mat-form-field>
        </div>

        <div class="column">
          <div>{{'Sampled By' | translate}}: <span>{{shownSampleDetails?.sampledby}}</span></div>
          <div>{{'Jurisdictions' | translate}}: <span>{{shownSampleDetails?.jurisdictionNames}}</span></div>
          @if (isFinalStep) {
            <div>
              <div class="pb-2"> {{'List Description Of Industry Or Process/Interferences Present In Sampling Area' |
              translate}}:</div>
              <span>{{labRequisitionFields.industryProcessInterferenceDescription}}</span>
            </div>
          }

          <mat-form-field appearance="outline" class="form-only-content">
            <mat-label>{{'List Description Of Industry Or Process/Interferences Present In Sampling Area' |
            translate}}</mat-label>
            <textarea matInput [(ngModel)]="labRequisitionFields.industryProcessInterferenceDescription" rows="3"
            [placeholder]="'Add Comments And Instructions Here' | translate"></textarea>
          </mat-form-field>
        </div>

      </div>
    </ng-template>

    <ng-template #finalize>
      <div id="finalize-view" class="finalize-view p-2 mb-2">
        <h4 class="coc-title text-center mb-4"> <b>{{'Chain Of Custody' | translate}}</b> </h4>
        @if (tenantLogoUrl) {
          <img class="tenant-image" [src]="tenantLogoUrl" alt="Tenant logo" width="50">
        }

        <div class="row">
          <div class="col-3 mt-n3">
            <ng-container *ngTemplateOutlet="tat"></ng-container>
          </div>
          <div class="col-9">
            <div class="row m-0 bordered-section mb-2">
              <div class="col-6 d-flex pl-0">
                <div class="material-grey-icon">
                  {{ (labDetails.labIntegrationType === IntegrationType.AlsLab
                  ? 'ALS Quote Number'
                  : 'Account Information') | translate }}:
                </div>
                <span class="pl-1">
                  {{ labDetails.labAccountNumber }}
                </span>
              </div>
              <div class="col-6 d-flex pr-0">
                <div class="material-grey-icon pl-3"> {{'Lab Requisition Number' | translate}}:</div>
                <span class="pl-1"> {{labRequisitionFields.labRequisitionNumber}} </span>
              </div>
            </div>
            <div class="row m-0">
              <div class="col-6 pl-0">
                <div class="bordered-section height-100-full">
                  <div>{{'Report To' | translate}}: <span>{{labDetails.reportTo}}</span></div>
                  <div>{{'Company Name' | translate}}: <span>{{labDetails.reportToCompanyName}}</span></div>
                  <div>{{'Address' | translate}}:
                    <span> {{labDetails.reportToAddress1}} {{labDetails.reportToAddress2}}</span>
                  </div>
                  <div>
                    {{'City' | translate}},
                    {{'State' | translate}},
                    {{'Zip' | translate}}:
                    <span>
                      {{labDetails.reportToCity}}
                      {{labDetails.reportToState}}
                      {{labDetails.reportToZip}}
                    </span>
                  </div>
                  <div>{{'Phone Number' | translate}}: <span>{{labDetails.reportToPhoneNumber}}</span></div>
                  <div>{{'Cell Number' | translate}}: <span>{{labDetails.reportToCellNumber}}</span></div>
                  <div>{{'Email' | translate}} (Report To): <span>{{labDetails.reportToEmail}}</span></div>
                  <div>{{'Email' | translate}} EDD: <span>{{labDetails.emailEdd}}</span></div>
                  <div class="comments">{{'Report To Comments' | translate}}:
                    <span>{{labRequisitionFields.reportToComments}}</span>
                  </div>
                </div>
              </div>

              <div class="col-6 pr-0">
                <div class="bordered-section height-100-full">
                  <div>{{'Invoice To' | translate}}: <span>{{labDetails.invoiceTo}}</span></div>
                  <div>{{'Company Name' | translate}}: <span>{{labDetails.invoiceCompanyName}}</span></div>
                  <div>{{'Address' | translate}}:
                    <span> {{labDetails.invoiceAddress1}} {{labDetails.invoiceAddress2}}</span>
                  </div>
                  <div>{{'Phone Number' | translate}}: <span>{{labDetails.invoicePhoneNumber}}</span></div>
                  <div>{{'Email' | translate}} (Invoice To): <span>{{labDetails.invoiceEmail}}</span></div>
                  <div>{{'PO' | translate}}: <span>{{labDetails.invoicePoNumber}}</span></div>
                  <div>{{'Payment Method' | translate}}: <span>{{labDetails.invoicePaymentInfo}}</span></div>
                  <div class="comments">{{'Invoice To Comments' | translate}}:
                    <span>{{labDetails.invoiceComments}}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-4">
          @if (shownSampleDetails) {
            <div class="row m-0 bordered-section">
              <div class="col-3 material-grey-icon wrap-text">{{'Site Name' | translate}}:
                <span>{{shownSampleDetails.siteNames}}</span>
              </div>
              <div class="col-3 material-grey-icon wrap-text">{{'Survey' | translate}}:
                <span>{{shownSampleDetails.surveyNames}}</span>
              </div>
              <div class="col-3 material-grey-icon wrap-text">{{'Sampled By' | translate}}:
                <span>{{shownSampleDetails.sampledby}}</span>
              </div>
              <div class="col-3 material-grey-icon wrap-text">{{'Jurisdictions' | translate}}:
                <span>{{shownSampleDetails.jurisdictionNames}}</span>
              </div>
            </div>
          }
          <div class="row m-0 mt-2">
            <div class="col-6 pl-0">
              <div class="bordered-section height-100-full">
                <div class="pb-2"> {{'General Comments' | translate}}:</div>
                <span>{{labRequisitionFields.generalComments}}</span>
              </div>
            </div>
            <div class="col-6 pr-0">
              <div class="bordered-section height-100-full">
                <div class="pb-2">
                  {{'List Description Of Industry Or Process/Interferences Present In Sampling Area' |
                translate}}:</div>
                <span>{{labRequisitionFields.industryProcessInterferenceDescription}}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="break-page"></div>
        <ng-container *ngTemplateOutlet="sampling"></ng-container>

        <div class="table-wrapper mt-4">
          <table class="table grouped-header-table">
            <thead>
              <tr>
                <th rowspan="2"></th>
                <th rowspan="2">{{'Print Name/Signature' | translate}}</th>
                <th colspan="2"></th>
                <th rowspan="2"></th>
                <th rowspan="2">{{'Print Name/Signature' | translate}}</th>
                <th colspan="2"></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{'Chain Of Custody' | translate}}</td>
                <td></td>
                <td>{{'Date' | translate}}</td>
                <td>{{'Time' | translate}}</td>
                <td></td>
                <td></td>
                <td>{{'Date' | translate}}</td>
                <td>{{'Time' | translate}}</td>
              </tr>
              <tr>
                <td>{{'Relinquished By' | translate}}:</td>
                <td class="coc-signoff-cell">{{ relinquishedBySignature }}</td>
                <td>{{ relinquishedByDate }}</td>
                <td>{{ relinquishedByTime }}</td>
                <td>{{'Received By' | translate}}:</td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>{{'Relinquished By' | translate}}:</td>
                <td></td>
                <td></td>
                <td></td>
                <td>{{'Received By' | translate}}:</td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-end material-grey-icon pt-2">
          {{labDetails.labName}}, {{labDetails.labAddress1}} {{labDetails.labAddress2}},
          {{labDetails.labCity}} {{labDetails.labState}} {{labDetails.labZip}},
          {{labDetails.labPhoneNumber}}
          @if (labDetails.labCellNumber) {
            |
          }
          {{labDetails.labCellNumber}}
        </div>
      </div>
    </ng-template>

    <ng-template #accountInfoBase let-ctx>
      <mat-form-field appearance="outline" class="w-50 form-only-content">
        <mat-label>{{'Lab Requisition Number' | translate}}</mat-label>
        <input matInput required maxlength="20" [(ngModel)]="labRequisitionFields.labRequisitionNumber"
          [disabled]="$safeNavigationMigration(labRequisitionFields?.isAutogenerated)" />
        </mat-form-field>
        @if (isFinalStep) {
          <div class="d-flex">
            <div class="pb-2 material-grey-icon"> {{'Lab Requisition Number' | translate}}:</div>
            <span class="pl-1"> {{labRequisitionFields.labRequisitionNumber}} </span>
          </div>
        }
        <div class="account-info grid-container mb-4">
          <div class="column">
            <div>{{(ctx.showAlsQuote ? 'ALS Quote Number' : 'Account Information') | translate}}: <span>{{labDetails.labAccountNumber}}</span></div>
            <div>{{'Email EDD' | translate}}: <span>{{labDetails.emailEdd}}</span></div>
            <div>{{'PO' | translate}}: <span>{{labDetails.invoicePoNumber}}</span></div>
            <div>{{'Invoice To' | translate}}: <span>{{labDetails.invoiceTo}} {{labDetails.invoiceCompanyName}}</span>
          </div>
          <div>{{'Email (Invoice To)' | translate}}: <span>{{labDetails.invoiceEmail}}</span></div>
          @if (isFinalStep) {
            <div>
              <div class="pb-2"> {{'Comments And Instructions' | translate}}:</div>
              <span>{{labRequisitionFields.invoiceComments}}</span>
            </div>
          }
          <mat-form-field appearance="outline" class="full-width form-only-content">
            <mat-label>{{'Comments And Instructions' | translate}}</mat-label>
            <textarea [(ngModel)]="labRequisitionFields.invoiceComments" matInput
              [placeholder]="'Add Comments And Instructions Here' | translate" rows="4">
            </textarea>
          </mat-form-field>
        </div>
        <div class="column">
          <div>{{'Payment Method' | translate}}: <span>{{labDetails.invoicePaymentInfo}}</span></div>
          <div>{{'Report To' | translate}}: <span>{{labDetails.reportTo}}</span></div>
          <div>{{'Email' | translate}}: <span>{{labDetails.reportToEmail}}</span></div>
          @if (isFinalStep) {
            <div>
              <div class="pb-2"> {{'Comments And Instructions' | translate}}:</div>
              <span>{{labRequisitionFields.reportToComments}}</span>
            </div>
          }
          <mat-form-field appearance="outline" class="full-width form-only-content">
            <mat-label>{{'Comments And Instructions' | translate}}</mat-label>
            <textarea [(ngModel)]="labRequisitionFields.reportToComments" matInput
              [placeholder]="'Add Comments And Instructions Here' | translate" rows="4">
            </textarea>
          </mat-form-field>
        </div>
      </div>
    </ng-template>